// music/states.jsx — S0 / S1 / S2 layouts.
//
// PR 2 round A (this commit): S0 grows Recently Played + Made For You
// rows from Spotify (via useSpotifyApi). Tap-to-pick on tiles bubbles
// to the parent — no dispatch yet (that's PR 3's target picker).
//
// PR 2 round B: Favourites + Sources rows.
// PR 2 round C: S1 search results.
// PR 2 round D: S2 detail view.

import * as React from 'react';
import { CoverArt, TYPE_ICON, usePinnedPlaylists, parseSpotifyPlaylistId } from '../shared.jsx';
import { A_FONT, ALabel, fs, BusyDot } from '../direction-a.jsx';
import { RADIUS } from '../theme.js';
import { Section, Carousel, ContentTile, SourceTile, SearchInput, TrackRow, SpeakerRow, PresetTile, SkeletonCarousel, SkeletonRow, IntegrationErrorBanner } from './primitives.jsx';
import { useSpotifyApi, useFetched } from '../hooks/use-spotify-api.jsx';
import { useSonosFavourites } from '../hooks/use-sonos-favourites.jsx';
import { appleLib, applePost } from '../hooks/use-apple-library.jsx';
import { payloadTracks, contentsWithheld, canListItems } from './spotify-shapes.js';
import { likedPlayList, LIKED_QUEUE_MAX, progressLabel, resumeMs, podcastRows } from './library-model.js';

const { useState, useEffect, useMemo } = React;

// Spotify image helper: tracks/albums/playlists embed an `images` array
// sorted descending by size. Pick the smallest that's at least `minSize`
// so we're not downloading 640px artwork for 120px tiles.
function pickImage(images, minSize = 200) {
  if (!Array.isArray(images) || images.length === 0) return null;
  const ascending = [...images].sort((a, b) => (a.width || 0) - (b.width || 0));
  const match = ascending.find((img) => (img.width || 0) >= minSize);
  return (match || ascending[ascending.length - 1])?.url || null;
}

// Tile shape adapters — map Spotify response objects into ContentTile
// props. Each spotify endpoint returns a slightly different envelope;
// these keep the rendering uniform.
//
// Null guards: Spotify can return null entries in tracks.items /
// albums.items / playlists.items / etc. (most commonly for content
// that's been pulled from the catalog but is still referenced by a
// playlist or recently-played history). Reading .id on those nulls
// triggers a React render-time crash that unmounts MusicShell and
// dumps the user back at HomeView — what looks like "page disappeared
// to home." Each adapter returns null when input is null/missing the
// expected fields, and callers .filter(Boolean) before rendering.
function trackToTile(item) {
  if (!item) return null;
  const track = item.track || item;  // recently-played wraps in {track, played_at}
  if (!track || !track.id) return null;
  return {
    id: track.id,
    title: track.name || '(untitled)',
    subtitle: track.artists?.map((a) => a?.name).filter(Boolean).join(', '),
    imageUrl: pickImage(track.album?.images),
    // S2's track path fetches the track's album so the user lands on a
    // populated detail page instead of a blank shell — needs the album
    // id to do that. Without this, every track tile from search /
    // recently-played routes through S2 and renders nothing.
    albumId: track.album?.id || null,
    seed: track.id,
    kind: 'track',
  };
}
function playlistToTile(pl) {
  if (!pl || !pl.id) return null;
  return {
    id: pl.id,
    title: pl.name || '(untitled playlist)',
    subtitle: pl.description || (pl.owner?.display_name ? `By ${pl.owner.display_name}` : ''),
    imageUrl: pickImage(pl.images),
    seed: pl.id,
    kind: 'playlist',
  };
}

function albumToTile(a) {
  if (!a || !a.id) return null;
  return {
    id: a.id,
    title: a.name || '(untitled album)',
    subtitle: a.artists?.map((x) => x?.name).filter(Boolean).join(', '),
    imageUrl: pickImage(a.images),
    seed: a.id,
    kind: 'album',
  };
}

function artistToTile(a) {
  if (!a || !a.id) return null;
  return {
    id: a.id,
    title: a.name || '(unknown artist)',
    subtitle: 'Artist',
    imageUrl: pickImage(a.images),
    seed: a.id,
    kind: 'artist',
  };
}

// Podcast episode tile — single playable item. Subtitle is the show
// name so users can disambiguate identically-named episodes across
// different shows. Episode images aren't always populated (some shows
// only set art at the show level); fall back to show.images.
function episodeToTile(ep) {
  if (!ep || !ep.id) return null;
  return {
    id: ep.id,
    title: ep.name || '(untitled episode)',
    subtitle: ep.show?.name || 'Episode',
    imageUrl: pickImage(ep.images) || pickImage(ep.show?.images),
    seed: ep.id,
    kind: 'episode',
  };
}

// Podcast show tile — a series. Tapping drills into S2's show detail
// which lists episodes. Subtitle is the publisher.
function showToTile(s) {
  if (!s || !s.id) return null;
  return {
    id: s.id,
    title: s.name || '(untitled show)',
    subtitle: s.publisher || 'Show',
    imageUrl: pickImage(s.images),
    seed: s.id,
    kind: 'show',
  };
}

// Episode rows reuse TrackRow for visual consistency. TrackRow reads
// `track.artists[*].name` for its subtitle line; we shim the release
// date into that slot so the row reads "Episode name / 15 Mar 2026 /
// 42:18". Pubdate is more useful than the show name in a show-detail
// view where every row is from the same show.
function shimEpisodeAsTrack(ep, progress = '') {
  if (!ep || !ep.id) return null;
  const line = [ep.release_date ? fmtReleaseDate(ep.release_date) : '', progress].filter(Boolean).join(' · ');
  return {
    id: ep.id,
    name: ep.name || '(untitled episode)',
    duration_ms: ep.duration_ms,
    images: ep.images && ep.images.length ? ep.images : undefined,
    artists: line ? [{ name: line }] : [],
  };
}

function fmtReleaseDate(iso) {
  if (!iso) return '';
  // Spotify release_date is YYYY-MM-DD (or sometimes just YYYY-MM).
  // Avoid Date parsing (timezone shifts) and format the parts directly.
  const [y, m, d] = iso.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const mon = m ? months[parseInt(m, 10) - 1] : '';
  return d ? `${parseInt(d, 10)} ${mon} ${y}` : `${mon} ${y}`.trim();
}

// Podcasts for the home rows: followed shows, then the latest five episodes
// of the most recently followed ten, cached at module scope for 30 minutes
// so reopening the card costs nothing. At most 11 Spotify calls per half hour
// per device, three at a time (memory: spotify-rate-limit-cascades).
const PODCAST_TTL_MS = 30 * 60 * 1000;
const PODCAST_SHOWS = 10;
let podcastCache = null; // { at, value }

function usePodcasts(spotify, enabled) {
  const [state, setState] = useState(() =>
    (podcastCache && Date.now() - podcastCache.at < PODCAST_TTL_MS)
      ? { ...podcastCache.value, loading: false, error: null }
      : { shows: [], continueListening: [], fresh: [], loading: true, error: null });
  useEffect(() => {
    if (!enabled) return undefined;
    if (podcastCache && Date.now() - podcastCache.at < PODCAST_TTL_MS) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const entries = await spotify.savedShows();
        const shows = entries.map((e) => e?.show).filter((s) => s && s.id);
        const recent = shows.slice(0, PODCAST_SHOWS);
        const withEpisodes = [];
        for (let i = 0; i < recent.length; i += 3) {
          const batch = recent.slice(i, i + 3);
          const pages = await Promise.all(batch.map((show) =>
            spotify.showEpisodes(show.id).catch(() => null)));
          pages.forEach((pg, j) => withEpisodes.push({ show: batch[j], episodes: (pg?.items || []).filter(Boolean) }));
        }
        const value = { shows, ...podcastRows(withEpisodes) };
        podcastCache = { at: Date.now(), value };
        if (!cancelled) setState({ ...value, loading: false, error: null });
      } catch (error) {
        if (!cancelled) setState((s) => ({ ...s, loading: false, error }));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
  return state;
}

// Episode tile for the home rows: subtitle carries the show and the
// listener's progress ("Moonshots · 1 h 35 min left") or release date.
function episodeHomeTile(ep, withProgress) {
  const tile = episodeToTile(ep);
  if (!tile) return null;
  const extra = withProgress ? progressLabel(ep) : fmtReleaseDate(ep.release_date);
  return { ...tile, subtitle: [ep.show?.name, extra].filter(Boolean).join(' · ') };
}

export function S0({ T, hass, getToken, onPickItem, onPickSource, onOpenFavourites, onOpenLibrary, onOpenApple, onOpenBuilder }) {
  const spotify = useSpotifyApi(getToken);
  const pods = usePodcasts(spotify, !!getToken);
  const playEpisode = (ep) => {
    const tile = episodeToTile(ep);
    if (tile) onPickItem?.(tile, { play: true, seekMs: resumeMs(ep) });
  };
  const episodeRow = (eyebrow, eps, withProgress) => eps.length > 0 && (
    <Section T={T} eyebrow={eyebrow} icon={TYPE_ICON.episode}>
      <Carousel>
        {eps.map((ep, i) => {
          const tile = episodeHomeTile(ep, withProgress);
          return tile && (
            <ContentTile key={`${tile.id}-${i}`} T={T}
              title={tile.title} subtitle={tile.subtitle}
              imageUrl={tile.imageUrl} seed={tile.seed}
              onClick={() => playEpisode(ep)}/>
          );
        })}
      </Carousel>
    </Section>
  );

  // 'Your top mixes' — top artists, rendered as artist tiles. Tap
  // drills into the artist's top tracks in S2. Playlists / albums /
  // artists from the user's library now live inside the Library
  // drill-in (opened via the pill above), so S0 stays focused on
  // discovery rows rather than mirroring the library twice.
  const mixes = useFetched(() => spotify.topArtists(20), [getToken]);
  const favs = useSonosFavourites(hass, getToken);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Library pill row — entry points to the Sonos browsers. */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {favs.favourites.length > 0 && onOpenFavourites && (
          <LibraryPill T={T} onClick={onOpenFavourites}
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                <path d="M12 17.3l-5.4 3 1.4-6.1L3 9.9l6.2-.5L12 4l2.8 5.4 6.2.5-5 4.3 1.4 6.1z"/>
              </svg>
            }
            label="Favourites"/>
        )}
        {onOpenLibrary && (
          <LibraryPill T={T} onClick={onOpenLibrary}
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18V5l12-2v13"/>
                <circle cx="6" cy="18" r="3"/>
                <circle cx="18" cy="16" r="3"/>
              </svg>
            }
            label="Spotify"/>
        )}
        {onOpenApple && (
          <LibraryPill T={T} onClick={onOpenApple}
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55a4 4 0 1 0 4 4V7h4V3h-6z"/>
              </svg>
            }
            label="Apple Music"/>
        )}
        {onOpenBuilder && (
          <LibraryPill T={T} onClick={onOpenBuilder}
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9"/>
                <path d="M12 8v8M8 12h8"/>
              </svg>
            }
            label="Build a playlist"/>
        )}
      </div>

      {/* Spotify integration error — surfaces above the carousels so
          the user sees it before they tap into a row that may also be
          empty. Sources tile keeps rendering even on error (it doesn't
          depend on Spotify). */}
      {mixes.error && (
        <IntegrationErrorBanner T={T}
          title="Spotify is unreachable"
          message={mixes.error?.message?.slice(0, 200)}
          onRetry={() => mixes.refetch?.()}/>
      )}

      {/* Your top mixes — top artists. Tap drills into S2 artist view
          which surfaces their top tracks. Above Sources because it's
          the discovery surface users actually browse first; Sources is
          a niche affordance (line-in only) that doesn't need top-of-
          carousel real estate. Skeleton placeholder while mixes is in
          flight (only on the first load — refetches keep the prior
          tiles visible). */}
      {mixes.loading && !mixes.data && !mixes.error && (
        <Section T={T} eyebrow="Your top mixes">
          <SkeletonCarousel T={T}/>
        </Section>
      )}
      {mixes.data && mixes.data.items?.length > 0 && (() => {
        const tiles = mixes.data.items.map(artistToTile).filter(Boolean);
        return tiles.length > 0 && (
          <Section T={T} eyebrow="Your top mixes">
            <Carousel>
              {tiles.map((tile, i) => (
                <ContentTile key={`${tile.id}-${i}`} T={T}
                  title={tile.title} subtitle={tile.subtitle}
                  imageUrl={tile.imageUrl} seed={tile.seed}
                  onClick={() => onPickItem?.(tile)}/>
              ))}
            </Carousel>
          </Section>
        );
      })()}

      {/* Podcasts — Spotify's own resume points: part-played episodes
          resume where they were left, on any device. */}
      {episodeRow('Continue listening', pods.continueListening, true)}
      {episodeRow('New episodes', pods.fresh, false)}
      {pods.shows.length > 0 && (
        <Section T={T} eyebrow="Your podcasts" icon={TYPE_ICON.show}>
          <Carousel>
            {pods.shows.map(showToTile).filter(Boolean).map((tile, i) => (
              <ContentTile key={`${tile.id}-${i}`} T={T}
                title={tile.title} subtitle={tile.subtitle}
                imageUrl={tile.imageUrl} seed={tile.seed}
                onClick={() => onPickItem?.(tile, { open: true })}/>
            ))}
          </Carousel>
        </Section>
      )}

      {/* Sources — line-in (currently only Office Turntable). Single
          tile that opens the line-in picker in PR 5; PR 2 stubs to a
          placeholder. Always renders; this row never hides. */}
      <Section T={T} eyebrow="Sources">
        <Carousel>
          <SourceTile T={T} label="Line-in"
            onClick={() => onPickSource?.({ kind: 'line-in' })}/>
        </Carousel>
      </Section>
    </div>
  );
}

// Library-row pill — entry point into a drill-in screen (Favourites
// list / Sonos Library browser). A quiet key, the same chrome as the
// in-hero action pills (Tune Balance / Group) so the visual vocabulary
// is consistent. The icon takes the label's colour.
function LibraryPill({ T, icon, label, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 10,
      height: 48, padding: '0 18px', borderRadius: RADIUS.control,
      border: 'none', background: T.cardSoft,
      color: T.ink, cursor: 'pointer',
      fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ color: 'inherit', display: 'flex' }}>{icon}</span>
      {label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// S1 — search results.
//
// Sticky SearchInput at top; grouped results below (Top result · Tracks
// · Albums · Artists · Playlists). Empty state when no query; results
// load on a 250ms debounce. Tap a result → onPickItem(tile) bubbles
// up; shell.jsx transitions to S2 with that tile as the item.
// ─────────────────────────────────────────────────────────────────────

export function S1({ T, getToken, query, onChangeQuery, onPickItem }) {
  const spotify = useSpotifyApi(getToken);

  // 250ms debounce — keep the input responsive but don't hammer Spotify.
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const results = useFetched(
    () => debouncedQuery ? spotify.search(debouncedQuery) : Promise.resolve(null),
    [debouncedQuery]
  );

  const tracks    = results.data?.tracks?.items    || [];
  const albums    = results.data?.albums?.items    || [];
  const artists   = results.data?.artists?.items   || [];
  const playlists = results.data?.playlists?.items || [];
  const episodes  = results.data?.episodes?.items  || [];
  const shows     = results.data?.shows?.items     || [];
  const topTrack  = tracks[0];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <SearchInput T={T} value={query} onChange={onChangeQuery} onClear={() => onChangeQuery('')}/>

      {/* Empty state — no query yet. Inviting hint, not an error. */}
      {!debouncedQuery && (
        <div style={{
          fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
          color: T.inkSoft, padding: '32px 0', textAlign: 'center',
        }}>
          What would you like to play?
        </div>
      )}

      {/* Skeleton placeholder while a search is in flight. Only on the
          first response for the current query — when the user types
          another character, `results.data` is briefly stale; we keep
          showing the prior results to avoid a flicker. */}
      {results.loading && debouncedQuery && !results.data && !results.error && (
        <>
          <Section T={T} eyebrow="Top result">
            <SkeletonCarousel T={T} count={1} size={180}/>
          </Section>
          <Section T={T} eyebrow="Tracks" icon={TYPE_ICON.track}>
            <div>
              <SkeletonRow T={T}/>
              <SkeletonRow T={T}/>
              <SkeletonRow T={T}/>
            </div>
          </Section>
        </>
      )}

      {results.error && (
        <IntegrationErrorBanner T={T}
          title="Spotify search failed"
          message={results.error?.message?.slice(0, 200)}
          onRetry={() => results.refetch?.()}/>
      )}

      {results.data && debouncedQuery && (() => {
        // Filter null entries up-front — Spotify can return nulls for
        // content that's been pulled but is still referenced.
        const safeTracks    = tracks.filter(Boolean);
        const safeAlbums    = albums.filter(Boolean);
        const safeArtists   = artists.filter(Boolean);
        const safePlaylists = playlists.filter(Boolean);
        const safeEpisodes  = episodes.filter(Boolean);
        const safeShows     = shows.filter(Boolean);
        const safeTopTrack  = safeTracks[0] && safeTracks[0].id ? safeTracks[0] : null;
        const albumTiles    = safeAlbums.slice(0, 10).map(albumToTile).filter(Boolean);
        const artistTiles   = safeArtists.slice(0, 10).map(artistToTile).filter(Boolean);
        const playlistTiles = safePlaylists.slice(0, 10).map(playlistToTile).filter(Boolean);
        const episodeTiles  = safeEpisodes.slice(0, 10).map(episodeToTile).filter(Boolean);
        const showTiles     = safeShows.slice(0, 10).map(showToTile).filter(Boolean);
        const trackRowList  = safeTracks.slice(1, 6).filter((t) => t && t.id);
        const hasAnything =
          safeTopTrack || trackRowList.length > 0 ||
          albumTiles.length > 0 || artistTiles.length > 0 || playlistTiles.length > 0 ||
          episodeTiles.length > 0 || showTiles.length > 0;

        return (
          <>
            {/* Top result — first track rendered larger. */}
            {safeTopTrack && (
              <Section T={T} eyebrow="Top result">
                <ContentTile T={T} size={180}
                  title={safeTopTrack.name}
                  subtitle={safeTopTrack.artists?.map((a) => a?.name).filter(Boolean).join(', ')}
                  imageUrl={pickImage(safeTopTrack.album?.images)}
                  seed={safeTopTrack.id}
                  onClick={() => onPickItem(trackToTile({ track: safeTopTrack }))}/>
              </Section>
            )}

            {/* Tracks list — skip index 0 (already shown as Top result). */}
            {trackRowList.length > 0 && (
              <Section T={T} eyebrow="Tracks" icon={TYPE_ICON.track}>
                <div>
                  {trackRowList.map((t) => (
                    <TrackRow key={t.id} T={T} track={t}
                      onClick={() => onPickItem(trackToTile({ track: t }))}/>
                  ))}
                </div>
              </Section>
            )}

            {albumTiles.length > 0 && (
              <Section T={T} eyebrow="Albums" icon={TYPE_ICON.album}>
                <Carousel>
                  {albumTiles.map((tile) => (
                    <ContentTile key={tile.id} T={T}
                      title={tile.title} subtitle={tile.subtitle}
                      imageUrl={tile.imageUrl} seed={tile.seed}
                      onClick={() => onPickItem(tile)}/>
                  ))}
                </Carousel>
              </Section>
            )}

            {artistTiles.length > 0 && (
              <Section T={T} eyebrow="Artists" icon={TYPE_ICON.artist}>
                <Carousel>
                  {artistTiles.map((tile) => (
                    <ContentTile key={tile.id} T={T}
                      title={tile.title} subtitle={tile.subtitle}
                      imageUrl={tile.imageUrl} seed={tile.seed}
                      onClick={() => onPickItem(tile)}/>
                  ))}
                </Carousel>
              </Section>
            )}

            {playlistTiles.length > 0 && (
              <Section T={T} eyebrow="Playlists" icon={TYPE_ICON.playlist}>
                <Carousel>
                  {playlistTiles.map((tile) => (
                    <ContentTile key={tile.id} T={T}
                      title={tile.title} subtitle={tile.subtitle}
                      imageUrl={tile.imageUrl} seed={tile.seed}
                      onClick={() => onPickItem(tile)}/>
                  ))}
                </Carousel>
              </Section>
            )}

            {showTiles.length > 0 && (
              <Section T={T} eyebrow="Shows" icon={TYPE_ICON.show}>
                <Carousel>
                  {showTiles.map((tile) => (
                    <ContentTile key={tile.id} T={T}
                      title={tile.title} subtitle={tile.subtitle}
                      imageUrl={tile.imageUrl} seed={tile.seed}
                      onClick={() => onPickItem(tile)}/>
                  ))}
                </Carousel>
              </Section>
            )}

            {episodeTiles.length > 0 && (
              <Section T={T} eyebrow="Episodes" icon={TYPE_ICON.episode}>
                <Carousel>
                  {episodeTiles.map((tile) => (
                    <ContentTile key={tile.id} T={T}
                      title={tile.title} subtitle={tile.subtitle}
                      imageUrl={tile.imageUrl} seed={tile.seed}
                      onClick={() => onPickItem(tile)}/>
                  ))}
                </Carousel>
              </Section>
            )}

            {/* No-results state — serif title + sans-serif hint.
                Spotify search is fuzzy enough that "no results" almost
                always means a typo or a track / album that isn't on
                Spotify (live recording, classical performance variant,
                etc.) rather than a system error. */}
            {!hasAnything && (
              <div style={{
                padding: '40px 16px', textAlign: 'center',
                display: 'flex', flexDirection: 'column', gap: 8,
                alignItems: 'center',
              }}>
                <div style={{
                  fontFamily: A_FONT.serif, fontWeight: 500,
                  fontSize: fs(T, 26), color: T.ink, lineHeight: 1.15,
                }}>
                  Nothing found for &ldquo;{debouncedQuery}&rdquo;.
                </div>
                <div style={{
                  fontFamily: A_FONT.sans, fontSize: fs(T, 14),
                  color: T.inkSoft, lineHeight: 1.45, maxWidth: 380,
                }}>
                  Try a different artist, album, or playlist name —
                  Spotify search is forgiving on spelling but strict on
                  punctuation.
                </div>
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// S2 — detail view.
//
// Three sub-types share the same shell: album, playlist, artist. Track
// items route to their album (so tapping a track in S1 opens that
// track's album, with the track in the list).
//
// Header: cover + eyebrow (kind) + serif title + subtitle + Play-all
// CTA. Body: TrackRow list. Tap "Play all" or any track → "Send to…
// arrives in PR 3" placeholder (no dispatch in PR 2; PR 3 wires S3).
// ─────────────────────────────────────────────────────────────────────

export function S2({ T, getToken, item, onPickItem, inRoomFocus = false }) {
  const spotify = useSpotifyApi(getToken);

  const detail = useFetched(async () => {
    if (!item) return null;
    if (item.kind === 'album')    return spotify.getAlbum(item.id);
    if (item.kind === 'playlist') return spotify.getPlaylist(item.id);
    if (item.kind === 'episode')  return spotify.getEpisode(item.id);
    if (item.kind === 'show')     return spotify.getShow(item.id);
    if (item.kind === 'liked')    return { liked: await spotify.likedTracks() };
    if (item.kind === 'artist') {
      // /artists/{id}/top-tracks started returning 403 for our app
      // token (2026-05-24) — Spotify has been quietly tightening
      // endpoint access for client-credential tokens (see Nov 2024
      // and subsequent restrictions). Fall back to /search?q=<name>
      // &type=track, filtered to tracks where this artist is a
      // contributor. Loses the editorial "official top tracks"
      // ordering but is functional and returns dispatchable tracks.
      try {
        return await spotify.getArtistTopTracks(item.id);
      } catch (e) {
        console.warn('[music/s2] artist top-tracks failed, falling back to search', e?.message || e);
        const result = await spotify.search(item.title, 'track', 'AU');
        const tracks = (result?.tracks?.items || []).filter((t) =>
          (t.artists || []).some((a) => a.id === item.id)
        );
        return { tracks };
      }
    }
    // 'track' (or anything unrecognised) → resolve the track's album so
    // the user sees the full record around the picked track.
    if (item.kind === 'track' && item.albumId) return spotify.getAlbum(item.albumId);
    return null;
  }, [item?.id, item?.kind]);

  if (!item) return null;

  if (detail.loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500, color: T.inkSoft,
      }}><BusyDot T={T}/>Loading {item.kind}…</div>
    );
  }

  if (detail.error) {
    return (
      <div style={{
        fontFamily: A_FONT.sans, fontSize: fs(T, 14), color: T.attention, lineHeight: 1.45,
      }}>
        Couldn&rsquo;t load {item.kind}: {detail.error.message?.slice(0, 140)}
      </div>
    );
  }

  // Play-button label: room-first → direct dispatch (label says what
  // it actually does); content-first → opens S3 ("Send to…" framing).
  const playLabel = inRoomFocus
    ? (item.kind === 'playlist' ? 'Play playlist'
       : item.kind === 'artist'   ? 'Play top tracks'
       : item.kind === 'track'    ? 'Play track'
       : item.kind === 'episode'  ? 'Play episode'
       : item.kind === 'show'     ? 'Play show'
       : item.kind === 'liked'    ? 'Shuffle play'
       : 'Play album')
    : 'Send to…';

  if (!detail.data) {
    // Track-without-album safety net — Spotify search tiles should now
    // always carry albumId (see trackToTile), but if a track ever
    // arrives without it (local file, podcast track, future shape
    // change) fall back to a single-track detail surface so the user
    // gets a Play button instead of a blank screen.
    if (item.kind === 'track') {
      return (
        <DetailLayout T={T} item={item}
          eyebrow="Track" eyebrowIcon={TYPE_ICON.track} title={item.title}
          subtitle={item.subtitle} imageUrl={item.imageUrl}
          playLabel={playLabel}
          onPlayAll={() => onPickItem?.(item, { play: true })}/>
      );
    }
    return null;
  }

  const data = detail.data;

  // Liked Songs: Sonos can't play Spotify's collection itself, so a play is
  // one track dispatched plus up to 49 queued after it (the artist path's
  // queueAfter). Shuffle samples 50; a row tap plays from that row on.
  if (item.kind === 'liked') {
    return <LikedSongs T={T} item={item} entries={data.liked || []}
      playLabel={playLabel} onPickItem={onPickItem}/>;
  }

  // Artist sub-type: data is { tracks: [...] } from /artists/{id}/top-tracks.
  // Tracks each carry their own album.images, so no cover patching needed.
  // "Play all" for artist dispatches the first top track (Spotify's
  // artist URI isn't directly playable on Sonos; tracks are).
  if (item.kind === 'artist') {
    const tracks = data.tracks || [];
    const firstTrack = tracks[0];
    // "Play all" dispatches the first track (play_media replaces the
    // current queue and starts playing) then passes the remaining
    // tracks as opts.queueAfter — shell.jsx's onPickItem +
    // onS3Dispatch sequentially enqueue=add each one so the
    // dispatched target gets the full artist set in order. Per-track
    // taps still play just that single track (no queueAfter) so the
    // tap-this-now semantics stay intact.
    const queueAfter = tracks.slice(1).map((t) => trackToTile({ track: t }));
    return (
      <DetailLayout T={T} item={item}
        eyebrow="Artist" eyebrowIcon={TYPE_ICON.artist} title={item.title}
        subtitle="Top tracks"
        imageUrl={item.imageUrl}
        playLabel={playLabel}
        onPlayAll={() => firstTrack && onPickItem?.(
          trackToTile({ track: firstTrack }),
          { play: true, queueAfter }
        )}>
        {tracks.map((t, i) => (
          <TrackRow key={t.id} T={T} track={t} showIndex index={i}
            onClick={() => onPickItem?.(trackToTile({ track: t }), { play: true })}/>
        ))}
      </DetailLayout>
    );
  }

  // Episode sub-type — single playable item, no track list. Renders
  // the same single-item shape as the track-without-album fallback.
  // Dispatches via opts.play=true so shell.jsx routes it through the
  // direct-dispatch path (no S3 detour when in room-first; S3 picker
  // in content-first, same as a track).
  if (item.kind === 'episode') {
    const heroImage = pickImage(data.images) || pickImage(data.show?.images) || item.imageUrl;
    const showName  = data.show?.name || item.subtitle || 'Episode';
    const dispatchItem = { ...item, imageUrl: heroImage, title: data.name || item.title, subtitle: showName };
    return (
      <DetailLayout T={T} item={item}
        eyebrow="Episode" eyebrowIcon={TYPE_ICON.episode} title={data.name || item.title}
        subtitle={showName} imageUrl={heroImage}
        playLabel={playLabel}
        onPlayAll={() => onPickItem?.(dispatchItem, { play: true })}/>
    );
  }

  // Show sub-type — list of episodes. Tapping the Play CTA dispatches
  // the show URI (Sonos picks the most-recent / continue-listening
  // episode based on its Spotify account history). Tapping an
  // individual episode row dispatches just that episode.
  //
  // Episodes are reverse-chronological from Spotify (newest first),
  // which matches user expectation for a podcast detail view.
  if (item.kind === 'show') {
    const heroImage = pickImage(data.images) || item.imageUrl;
    const publisher = data.publisher || 'Show';
    const episodes  = (data.episodes?.items || []).filter(Boolean);
    const dispatchItem = { ...item, imageUrl: heroImage, title: data.name || item.title };
    return (
      <DetailLayout T={T} item={item}
        eyebrow="Show" eyebrowIcon={TYPE_ICON.show} title={data.name || item.title}
        subtitle={publisher} imageUrl={heroImage}
        playLabel={playLabel}
        onPlayAll={() => onPickItem?.(dispatchItem, { play: true })}>
        {episodes.map((ep, i) => (
          <TrackRow key={`${ep.id}-${i}`} T={T} track={shimEpisodeAsTrack(ep, progressLabel(ep))} showIndex index={i}
            onClick={() => {
              // Per-episode tap dispatches that specific episode,
              // attaching the show's cover so the post-dispatch S4
              // hero has art even if the episode has no own image.
              // A part-played episode resumes at Spotify's resume point.
              const tile = episodeToTile(ep);
              if (tile && !tile.imageUrl) tile.imageUrl = heroImage;
              onPickItem?.(tile, { play: true, seekMs: resumeMs(ep) });
            }}/>
        ))}
      </DetailLayout>
    );
  }

  // Album / playlist (and track→album fallback) share this shape.
  const heroImage = pickImage(data.images) || item.imageUrl;
  // Spotify's /v1/albums/{id} returns simplified track objects WITHOUT
  // an `album` field (since they're already in the album context). The
  // TrackRow cover lookup then falls back to a CoverArt gradient. Patch
  // the parent album's images onto each track so the album cover shows
  // for every row. Playlist tracks already include album.images.
  const albumImagesForFallback = data.images || [];
  // payloadTracks reads both the Feb 2026 playlist shape (items.items[].item)
  // and the album / pre-2026 shape (tracks.items).
  const tracks = payloadTracks(data)
    .map((t) => (t.album ? t : { ...t, album: { images: albumImagesForFallback } }));
  // A followed playlist comes back with no contents at all in Development
  // Mode; say so rather than render an empty list. Play all still works.
  const withheld = item.kind === 'playlist' && contentsWithheld(data);

  const eyebrow = item.kind === 'playlist' ? 'Playlist' : 'Album';
  const subtitle = item.kind === 'playlist'
    ? (data.owner?.display_name ? `By ${data.owner.display_name}` : 'Playlist')
    : (data.artists?.map((a) => a.name).join(', ') || '');

  // The dispatch payload — the album/playlist itself, with its real
  // images so the post-dispatch S4 hero shows the right cover.
  const dispatchItem = { ...item, imageUrl: heroImage, title: data.name || item.title };

  return (
    <DetailLayout T={T} item={item}
      eyebrow={eyebrow} eyebrowIcon={TYPE_ICON[item.kind]} title={data.name || item.title}
      subtitle={subtitle} imageUrl={heroImage}
      playLabel={playLabel}
      onPlayAll={() => onPickItem?.(dispatchItem, { play: true })}>
      {withheld && (
        <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 14), color: T.inkSoft, lineHeight: 1.5, padding: '8px 4px' }}>
          Spotify only lists the tracks of playlists you made or share. This one still plays in full from the button above.
        </div>
      )}
      {tracks.map((t, i) => (
        <TrackRow key={`${t.id}-${i}`} T={T} track={t} showIndex index={i}
          onClick={() => {
            // Tap a track → dispatch the album/playlist starting from
            // this track's index. shell.jsx's onPickItem handles the
            // "skip N tracks after play_media settles" dance.
            onPickItem?.(dispatchItem, { startIndex: i, play: true });
          }}/>
      ))}
    </DetailLayout>
  );
}

function LikedSongs({ T, item, entries, playLabel, onPickItem }) {
  const [shown, setShown] = useState(100);
  const tracks = useMemo(
    () => entries.map((e) => e?.track).filter((t) => t && t.id && !t.is_local),
    [entries]);
  const play = (list) => {
    if (!list.length) return;
    onPickItem?.(trackToTile({ track: list[0] }),
      { play: true, queueAfter: list.slice(1).map((t) => trackToTile({ track: t })).filter(Boolean) });
  };
  return (
    <DetailLayout T={T} item={item}
      eyebrow="Library" eyebrowIcon={TYPE_ICON.track} title="Liked Songs"
      subtitle={tracks.length ? `${tracks.length} most recent · plays ${LIKED_QUEUE_MAX} at a time` : 'Nothing liked yet'}
      imageUrl={null}
      playLabel={playLabel}
      onPlayAll={() => play(likedPlayList(tracks, { shuffle: true }))}>
      {tracks.slice(0, shown).map((t, i) => (
        <TrackRow key={`${t.id}-${i}`} T={T} track={t} showIndex index={i}
          onClick={() => play(likedPlayList(tracks, { startIndex: i }))}/>
      ))}
      {tracks.length > shown && (
        <button onClick={() => setShown((n) => n + 100)} style={quietKey(T, { alignSelf: 'flex-start', marginTop: 12, padding: '0 18px' })}>
          Show {Math.min(100, tracks.length - shown)} more
        </button>
      )}
    </DetailLayout>
  );
}

// ─────────────────────────────────────────────────────────────────────
// S3 — Target picker.
//
// Two sections — Groups (named presets) on top, then Single speakers
// grouped by area. Tap a target → onDispatch fires + MusicShell
// transitions to S0 with focus on the dispatched coordinator.
//
// Per §11.16: S3 is *only* reached via the content-first flow (drawer
// Music → tile → S3) or via the "Send to other speakers" affordance
// in S4/S5 (room-first wanting to extend). Room-first tile taps
// bypass S3 entirely — that branch lives in shell.jsx onPickItem.
//
// Speaker areas mirror the household's mental layout from
// HANDOFF-ha-av.md §2. Office Arc is rendered TV-busy when its Sonos
// source is 'TV' (per §11.10 / §6 caveats).
// ─────────────────────────────────────────────────────────────────────

const AREAS = [
  {
    label: 'Living areas',
    entities: ['media_player.pavilion', 'media_player.rumpus', 'media_player.office', 'media_player.desk', 'media_player.office_turntable'],
  },
  {
    label: 'Bedrooms',
    entities: ['media_player.master_bed', 'media_player.ensuite', 'media_player.connor_s_room', 'media_player.dylans_room', 'media_player.guest_bedroom'],
  },
  {
    label: 'Outdoors / utility',
    entities: ['media_player.pool', 'media_player.laundry'],
  },
  {
    label: 'Portable',
    entities: ['media_player.move', 'media_player.roam_2'],
  },
];

// Derive a SpeakerRow's `state` object from the speaker's live HA state.
// Pure: takes (speaker, haStates) and returns the badge shape SpeakerRow
// expects. No hooks — called inside S3's render.
function deriveSpeakerState(speaker, haStates) {
  const e = haStates?.[speaker.entity];
  const a = e?.attributes || {};
  const haState = e?.state || 'unavailable';

  // Office Arc is TV-busy when its Sonos source is 'TV' — the Arc has
  // ARC source-switching from the LG; while TV is in use the speaker
  // is dedicated to TV audio and shouldn't accept Spotify dispatch.
  if (speaker.entity === 'media_player.office' && a.source === 'TV') {
    return { kind: 'tv-busy', badge: 'TV — Apple TV' };
  }
  if (haState === 'unavailable') {
    return { kind: 'offline', badge: 'Offline' };
  }
  const groupMembers = a.group_members || [];
  const inGroup = groupMembers.length > 1;
  const isCoord = inGroup && groupMembers[0] === speaker.entity;

  if (haState === 'playing') {
    if (inGroup && !isCoord) {
      const coordName = groupMembers[0]?.split('.').pop() || 'group';
      return { kind: 'in-group', badge: `in group · ${coordName}` };
    }
    const title = a.media_title;
    return { kind: 'playing', badge: title ? `playing · ${title}` : 'playing' };
  }
  if (inGroup && !isCoord) {
    const coordName = groupMembers[0]?.split('.').pop() || 'group';
    return { kind: 'in-group', badge: `in group · ${coordName}` };
  }
  // Battery for portables — shown alongside idle.
  if (speaker.kind === 'portable' && a.battery_level != null) {
    return { kind: 'idle', badge: `idle · battery ${a.battery_level}%` };
  }
  return { kind: 'idle', badge: 'idle' };
}

export function S3({ T, item, speakers, hass, onDispatch, onBack, extend = false, coordinatorEntity = null }) {
  // Line-in dispatch uses select_source + join, NOT the preset helper
  // scripts (which call play_media with Spotify URIs and would clobber
  // the source switch). Whole-house / Pavilion+Pool presets are hidden
  // for line-in until a separate preset-expansion path is wired —
  // single-speaker + Currently-playing + Custom… cover the common use
  // cases (Custom lets the user fan line-in out to any combination of
  // speakers without needing pre-built groups).
  const isLineIn = item?.kind === 'line-in';

  // Custom multi-select sub-state. Enabled when the user picks the
  // "Custom…" preset; SpeakerRows become checkboxes, the Groups
  // section hides, and a sticky "Send to N speakers" CTA appears.
  // In extend mode the Custom… preset is offered too (lets the user
  // add several speakers at once instead of tapping one at a time).
  const [customMode, setCustomMode] = useState(false);
  const [customSelected, setCustomSelected] = useState(() => new Set());
  const toggleCustom = (entity) => {
    setCustomSelected((prev) => {
      const next = new Set(prev);
      if (next.has(entity)) next.delete(entity);
      else next.add(entity);
      return next;
    });
  };
  const cancelCustom = () => {
    setCustomMode(false);
    setCustomSelected(new Set());
  };
  const confirmCustom = () => {
    const entities = Array.from(customSelected);
    if (entities.length === 0) return;
    onDispatch({
      kind: 'custom',
      entities,
      // Pick the first selection as the coordinator. dispatch.jsx's
      // dispatchCustom expects a coordinator hint; first-selected is a
      // reasonable default (matches "the room I tapped first leads").
      coordinator: entities[0],
    });
  };

  // Build the presets list. "Currently playing" only surfaces when at
  // least one Sonos is in an active group (per §11.6). "Custom…" is
  // always offered (except in extend mode, where it appears in a
  // dedicated row above the speaker list instead of among presets,
  // since extend has no Groups section).
  const presets = useMemo(() => {
    const list = isLineIn ? [] : [
      {
        key: 'whole_house',
        title: 'Whole house',
        members: 'Pavilion + Master + Pool + Move + standalones',
      },
      {
        key: 'pavilion_pool',
        title: 'Pavilion + Pool',
        members: 'Pavilion + Pool + Move',
      },
    ];
    // Detect any active Sonos group.
    let activeGroup = null;
    for (const sp of speakers || []) {
      if (sp.groupMembers?.length > 1) {
        const coord = sp.groupMembers[0];
        if (!activeGroup || coord === sp.entity) {
          activeGroup = { coordinator: coord, members: sp.groupMembers };
        }
      }
    }
    if (activeGroup) {
      const names = activeGroup.members
        .map((m) => (speakers || []).find((s) => s.entity === m)?.name || m)
        .join(' + ');
      list.push({
        key: 'currently_playing',
        title: 'Currently playing',
        members: names,
        coordinator: activeGroup.coordinator,
        badge: 'Join in',
      });
    }
    // Custom… — terminal preset. Tap enters multi-select mode (handled
    // locally; no onDispatch call yet).
    list.push({
      key: 'custom',
      title: 'Custom…',
      members: 'Pick any combination of speakers',
    });
    return list;
  }, [speakers, isLineIn]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {/* Extend-mode notice — replaces the content mini-card. Tells
          the user that the current playback will continue on the
          coordinator and the picked target will just join in. */}
      {extend && (
        <div style={{
          padding: '18px 20px',
          background: T.card, borderRadius: RADIUS.card, boxShadow: T.cardShadow,
        }}>
          <ALabel T={T}>Extend current playback</ALabel>
          <div style={{
            fontFamily: A_FONT.sans, fontSize: fs(T, 14), color: T.ink,
            marginTop: 8, lineHeight: 1.45,
          }}>
            Tap a speaker to add it to the current group. Audio keeps
            playing on the coordinator and the new speaker joins in.
          </div>
        </div>
      )}

      {/* Chosen content mini-card. */}
      {!extend && item && (
        <div style={{
          display: 'flex', gap: 16, alignItems: 'center',
          padding: '16px 20px 16px 16px',
          background: T.card, borderRadius: RADIUS.card, boxShadow: T.cardShadow,
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: RADIUS.key, overflow: 'hidden',
            background: T.cardSoft, flexShrink: 0,
          }}>
            {item.imageUrl
              ? <img src={item.imageUrl} alt="" width={64} height={64}
                     style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}/>
              : <CoverArt seed={item.id || item.title} size={64} radius={RADIUS.key} label={false}/>}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <ALabel T={T}>
              Send {item.kind || 'content'} to
            </ALabel>
            <div style={{
              fontFamily: A_FONT.serif, fontWeight: 500, fontSize: fs(T, 24), color: T.ink,
              lineHeight: 1.15,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2,
            }}>{item.title}</div>
            {item.subtitle && (
              <div style={{ fontSize: fs(T, 13), color: T.inkSoft, marginTop: 2,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {item.subtitle}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Groups section — hidden in extend mode since presets reset
          the group structure (which would surprise a user who just
          wanted to add one speaker to their current playback). Also
          hidden in custom multi-select sub-state (replaced by the
          custom-mode header below). */}
      {!extend && !customMode && presets.length > 0 && (
        <Section T={T} eyebrow="Groups">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {presets.map((p) => (
              <PresetTile key={p.key} T={T}
                title={p.title} members={p.members} badge={p.badge}
                onClick={() => {
                  if (p.key === 'custom') {
                    setCustomMode(true);
                    return;
                  }
                  onDispatch({
                    kind: 'preset', preset: p.key, coordinator: p.coordinator,
                  });
                }}/>
            ))}
          </div>
        </Section>
      )}

      {/* Extend-mode shortcut into custom multi-select — extend mode
          has no Groups section, so the Custom… affordance hops up to
          a single-line option above the speaker list. */}
      {extend && !customMode && (
        <button onClick={() => setCustomMode(true)} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          height: 52, padding: '0 18px', borderRadius: RADIUS.control,
          border: 'none', background: T.cardSoft,
          color: T.ink, cursor: 'pointer', textAlign: 'left',
          fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
        }}>
          <span>Pick several speakers…</span>
          <span style={{ color: 'inherit', display: 'flex' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="1.7"
                 strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 6l6 6-6 6"/>
            </svg>
          </span>
        </button>
      )}

      {/* Custom multi-select header — only when customMode is on.
          Shows the running count + a back-out chevron. */}
      {customMode && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          padding: '4px 0 14px',
          borderBottom: `1px solid ${T.hair}`,
        }}>
          <div style={{ minWidth: 0 }}>
            <ALabel T={T}>
              Custom selection
            </ALabel>
            <div style={{
              fontFamily: A_FONT.sans, fontWeight: 500,
              fontSize: fs(T, 17), color: T.ink, lineHeight: 1.25, marginTop: 4,
            }}>
              {customSelected.size === 0
                ? 'Tap speakers to add them'
                : `${customSelected.size} speaker${customSelected.size === 1 ? '' : 's'} selected`}
            </div>
          </div>
          <button onClick={cancelCustom} style={{
            height: 44, padding: '0 20px', borderRadius: RADIUS.control, flexShrink: 0,
            border: 'none', background: T.cardSoft,
            color: T.ink, cursor: 'pointer',
            fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
          }}>
            Cancel
          </button>
        </div>
      )}

      {/* Speakers section — grouped by area. In extend mode, the
          coordinator's own row hides (you can't "add it to its own
          group"). In custom mode, each row is a checkbox; the
          coordinator (if any) stays visible but is auto-selected and
          disabled (can't be removed from its own group). */}
      <Section T={T} eyebrow={customMode ? 'Choose speakers' : extend ? 'Add a speaker' : 'Single speakers'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {AREAS.map((area) => {
            const rows = area.entities
              .map((eid) => (speakers || []).find((s) => s.entity === eid))
              .filter(Boolean);
            if (rows.length === 0) return null;
            return (
              <div key={area.label}>
                <ALabel T={T} style={{ color: T.mute, marginBottom: 4 }}>
                  {area.label}
                </ALabel>
                {rows.map((sp) => {
                  // In extend mode (non-custom): skip the coordinator's
                  // own row (can't add a speaker to its own group).
                  if (extend && !customMode && sp.entity === coordinatorEntity) return null;
                  const state = deriveSpeakerState(sp, hass?.states);
                  if (customMode) {
                    return (
                      <SpeakerRow key={sp.entity} T={T}
                        name={sp.name}
                        areaLabel={area.label}
                        state={state}
                        selectable
                        selected={customSelected.has(sp.entity)}
                        onClick={() => toggleCustom(sp.entity)}/>
                    );
                  }
                  return (
                    <SpeakerRow key={sp.entity} T={T}
                      name={sp.name}
                      areaLabel={area.label}
                      state={state}
                      onClick={() => onDispatch({
                        kind: 'speaker', entity: sp.entity,
                      })}/>
                  );
                })}
              </div>
            );
          })}
        </div>
      </Section>

      {/* Sticky confirm bar — only in custom mode. Position is fixed
          so it stays in view as the user scrolls the speaker list.
          Width matches the takeover shell's main column. */}
      {customMode && (
        <div style={{
          position: 'sticky', bottom: 0, marginTop: 12,
          padding: '12px 0',
          background: `linear-gradient(to bottom, transparent 0%, ${T.paper} 30%)`,
          display: 'flex', justifyContent: 'center',
        }}>
          <button onClick={confirmCustom} disabled={customSelected.size === 0}
            style={{
              height: 52, padding: '0 32px', borderRadius: RADIUS.control, border: 'none',
              background: customSelected.size === 0 ? T.cardSoft : T.primary,
              color: customSelected.size === 0 ? T.mute : T.primaryInk,
              cursor: customSelected.size === 0 ? 'not-allowed' : 'pointer',
              fontFamily: A_FONT.sans, fontWeight: 500,
              fontSize: fs(T, 15),
              boxShadow: customSelected.size === 0 ? 'none' : T.cardShadow,
              transition: 'background .12s, color .12s',
            }}>
            {extend
              ? (customSelected.size === 0 ? 'Pick speakers to add'
                : `Add ${customSelected.size} speaker${customSelected.size === 1 ? '' : 's'}`)
              : (customSelected.size === 0 ? 'Pick speakers to send to'
                : `Send to ${customSelected.size} speaker${customSelected.size === 1 ? '' : 's'}`)}
          </button>
        </div>
      )}
    </div>
  );
}

function DetailLayout({ T, item, eyebrow, eyebrowIcon, title, subtitle, imageUrl, onPlayAll, playLabel = 'Send to…', children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{
          width: 160, height: 160, borderRadius: RADIUS.control, overflow: 'hidden',
          flexShrink: 0, background: T.cardSoft, boxShadow: T.cardShadow,
        }}>
          {imageUrl
            ? <img src={imageUrl} alt="" width={160} height={160}
                   style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}/>
            : <CoverArt seed={item.id} size={160} radius={RADIUS.control} label={false}/>}
        </div>
        <div style={{ flex: 1, minWidth: 240 }}>
          <ALabel T={T} icon={eyebrowIcon}>{eyebrow}</ALabel>
          <h2 style={{
            fontFamily: A_FONT.serif, fontSize: fs(T, 32), fontWeight: 500,
            margin: '6px 0 6px', color: T.ink, letterSpacing: '-.01em', lineHeight: 1.1,
          }}>{title}</h2>
          {subtitle && (
            <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 14), color: T.inkSoft }}>
              {subtitle}
            </div>
          )}
          <button onClick={onPlayAll} style={{
            marginTop: 18, height: 48, padding: '0 24px', borderRadius: RADIUS.control,
            background: T.primary, color: T.primaryInk, border: 'none', cursor: 'pointer',
            fontFamily: A_FONT.sans, fontWeight: 500, fontSize: fs(T, 15),
          }}>
            {playLabel}
          </button>
        </div>
      </div>
      <div style={{ marginTop: 4 }}>{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// SFavourites — full-list drill-in for Sonos Favourites. Promoted
// from the S0 carousel into its own screen so radio stations, custom
// playlists, etc. all stay browseable even when there are more than
// fit in a horizontal scroller. Tap → dispatch via the existing
// favourite route in shell.jsx onPickItem.
// ─────────────────────────────────────────────────────────────────────

export function SFavourites({ T, hass, getToken, onPickItem }) {
  const favs = useSonosFavourites(hass, getToken);
  if (favs.loading) return <BrowseStatus T={T}>Loading favourites…</BrowseStatus>;
  if (favs.error)   return <BrowseStatus T={T}>Favourites failed to load.</BrowseStatus>;
  if (favs.favourites.length === 0) return <BrowseStatus T={T}>No favourites yet. Add some in the Sonos app.</BrowseStatus>;
  return (
    <BrowseGrid>
      {favs.favourites.map((f) => (
        <ContentTile key={f.id} T={T}
          title={f.title}
          subtitle={f.contentType === 'favorite_item_id' ? 'Sonos' : f.contentType}
          imageUrl={f.thumbnail}
          seed={f.id}
          onClick={() => onPickItem?.({
            id: f.id, title: f.title, kind: 'favourite',
            contentType: f.contentType, imageUrl: f.thumbnail,
          })}/>
      ))}
    </BrowseGrid>
  );
}

// ─────────────────────────────────────────────────────────────────────
// SLibrary — Spotify library drill-in with filter chips. Mirrors
// Spotify's web Your Library hierarchy: top-row chips select between
// Playlists / Artists / Albums; tile grid below renders the matching
// shape. Tap a tile → S2 detail (which already supports each kind).
// State (active filter) lives in the view object so back-nav restores
// it; default = Playlists on first open.
// ─────────────────────────────────────────────────────────────────────

const LIBRARY_FILTERS = [
  { key: 'playlists', label: 'Playlists' },
  { key: 'artists',   label: 'Artists' },
  { key: 'albums',    label: 'Albums' },
];

// Sort options for the Library grid. "Mine first" is playlists-only —
// albums are all "saved" and artists all "followed", so ownership has no
// meaning there. "Recently added" is the default (Spotify's native order;
// for albums we sort explicitly on added_at).
const SORT_OPTIONS = [
  { key: 'recent', label: 'Recently added' },
  { key: 'az',     label: 'A–Z' },
  { key: 'mine',   label: 'Mine first', playlistsOnly: true },
];

// Quiet key — the 2026-10 secondary button (quiet fill, no outline,
// sentence case). Shared by the drill-in screens below.
const quietKey = (T, extra) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  height: 44, padding: '0 18px', borderRadius: RADIUS.control,
  border: 'none', background: T.cardSoft, color: T.ink, cursor: 'pointer',
  fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
  whiteSpace: 'nowrap',
  ...extra,
});

const ChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden
       stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 6l-6 6 6 6"/>
  </svg>
);

// A pinned playlist tile with an unpin affordance. ContentTile is itself
// a <button>, so the ✕ is a sibling overlay (not nested) — stopPropagation
// keeps a tap on it from also opening the playlist. The button is a full
// 44px hit area in the cover's corner; the visible mark is the small
// scrim disc drawn inside it.
function PinnedTile({ T, tile, onPick, onRemove }) {
  return (
    <div style={{ position: 'relative', width: 'fit-content' }}>
      <ContentTile T={T} title={tile.title} subtitle={tile.subtitle}
        imageUrl={tile.imageUrl} seed={tile.seed} onClick={onPick}/>
      <button onClick={(e) => { e.stopPropagation(); onRemove(); }}
        aria-label={`Unpin ${tile.title}`} title="Unpin"
        style={{
          position: 'absolute', top: 0, right: 0, width: 44, height: 44,
          border: 'none', cursor: 'pointer', padding: 0,
          background: 'transparent', color: T.onPhoto,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
        <span style={{
          width: 28, height: 28, borderRadius: '50%', background: T.scrim,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden
               stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </span>
      </button>
    </div>
  );
}

// Paste-a-link bar for pinning playlists the library API won't return
// (Made For You mixes etc.). Resolves the link once via /v1/playlists/{id}
// — which works even for algorithmic mixes — then hands the resolved tile
// up to the pin store. Collapsed to a single button until tapped.
function PinLinkBar({ T, spotify, onPinned }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setError(null);
    const id = parseSpotifyPlaylistId(value);
    if (!id) { setError("That doesn't look like a Spotify playlist link."); return; }
    setBusy(true);
    try {
      const tile = playlistToTile(await spotify.getPlaylist(id));
      if (!tile) throw new Error('unresolvable');
      onPinned(tile);
      setValue(''); setOpen(false);
    } catch (e) {
      // Spotify 404s on Spotify-owned playlists (Made For You mixes,
      // Discover Weekly, editorial) for apps in Development Mode — the
      // Web API simply won't serve them (see spotify_app_in_dev_mode
      // notes). Those play fine through Sonos's own Spotify integration,
      // so point the user at the path that actually works.
      const msg = String(e?.message || '');
      if (msg.includes('Spotify 404')) {
        setError("Spotify blocks its own mixes (Daily Mix, Discover Weekly, “Made For You”) from this API. To use one here, favourite it in the Sonos app — it'll show under Favourites.");
      } else {
        setError("Couldn't load that playlist — check the link and try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} style={quietKey(T, { alignSelf: 'flex-start' })}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden
             stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 5v14M5 12h14"/>
        </svg>
        Pin a Spotify link
      </button>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input value={value} autoFocus
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder="Paste a Spotify playlist link…"
          style={{
            flex: '1 1 220px', minWidth: 0, height: 44, padding: '0 16px',
            borderRadius: RADIUS.control, boxSizing: 'border-box',
            border: 'none', outline: 'none',
            background: T.card, boxShadow: T.cardShadow, color: T.ink,
            fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
          }}/>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={submit} disabled={busy} style={quietKey(T, {
            background: busy ? T.keyFill : T.primary,
            color: busy ? T.ink : T.primaryInk,
            boxShadow: busy ? T.keyShadow : 'none',
            cursor: busy ? 'default' : 'pointer',
          })}>
            {busy && <BusyDot T={T}/>}
            {busy ? 'Pinning…' : 'Pin'}
          </button>
          <button onClick={() => { setOpen(false); setError(null); setValue(''); }}
            style={quietKey(T)}>Cancel</button>
        </div>
      </div>
      {error && (
        <div style={{ color: T.attention, fontFamily: A_FONT.sans, fontSize: fs(T, 13), lineHeight: 1.45 }}>{error}</div>
      )}
    </div>
  );
}

export function SLibrary({ T, getToken, onPickItem, filter, onFilterChange }) {
  const spotify = useSpotifyApi(getToken);
  const active = filter || 'playlists';
  const [sort, setSort] = useState('recent');
  const { pins, addPin, removePin } = usePinnedPlaylists();

  const playlists = useFetched(
    () => active === 'playlists' ? spotify.myPlaylists() : Promise.resolve(null),
    [getToken, active]
  );
  const artists = useFetched(
    () => active === 'artists' ? spotify.followedArtists() : Promise.resolve(null),
    [getToken, active]
  );
  const albums = useFetched(
    () => active === 'albums' ? spotify.savedAlbums() : Promise.resolve(null),
    [getToken, active]
  );
  // "Mine first" needs the current user's id to tell owned playlists from
  // followed ones. Only fetched when that sort is actually selected.
  const me = useFetched(
    () => (active === 'playlists' && sort === 'mine') ? spotify.getMe() : Promise.resolve(null),
    [getToken, active, sort]
  );
  const myId = me.data?.id || null;

  // Sort options valid for the active filter; fall back to "recent" if the
  // current selection isn't offered here (e.g. "mine" while on Albums).
  const sortOptions = SORT_OPTIONS.filter((o) => !o.playlistsOnly || active === 'playlists');
  const effectiveSort = sortOptions.some((o) => o.key === sort) ? sort : 'recent';

  const byName = (get) => (a, b) =>
    (get(a) || '').localeCompare(get(b) || '', undefined, { sensitivity: 'base' });

  let tiles = [];
  let loading = false;
  let error = null;
  if (active === 'playlists') {
    loading = playlists.loading; error = playlists.error;
    // Pinned playlists render in their own row above; drop them from the
    // main grid so one that's also in the library doesn't appear twice.
    const pinnedIds = new Set(pins.map((p) => p.id));
    const items = (playlists.data?.items || []).filter((pl) => pl && !pinnedIds.has(pl.id));
    if (effectiveSort === 'az') items.sort(byName((pl) => pl?.name));
    else if (effectiveSort === 'mine' && myId) {
      // Stable sort keeps Spotify's order within each group.
      items.sort((a, b) =>
        (a?.owner?.id === myId ? 0 : 1) - (b?.owner?.id === myId ? 0 : 1));
    }
    tiles = items.map(playlistToTile).filter(Boolean);
  } else if (active === 'artists') {
    loading = artists.loading; error = artists.error;
    const items = [...(artists.data?.items || [])];
    if (effectiveSort === 'az') items.sort(byName((a) => a?.name));
    tiles = items.map(artistToTile).filter(Boolean);
  } else if (active === 'albums') {
    loading = albums.loading; error = albums.error;
    // /me/albums wraps each entry in { added_at, album }; unwrap to album.
    const entries = [...(albums.data?.items || [])];
    if (effectiveSort === 'az') entries.sort(byName((e) => e?.album?.name));
    else entries.sort((a, b) =>
      String(b?.added_at || '').localeCompare(String(a?.added_at || '')));
    tiles = entries.map((entry) => albumToTile(entry?.album)).filter(Boolean);
  }

  const hasPins = active === 'playlists' && pins.length > 0;
  const showEmpty = !loading && !error && tiles.length === 0 && !hasPins;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <BrowseControls T={T}
        filters={LIBRARY_FILTERS} filter={active} onFilter={(k) => onFilterChange?.(k)}
        sorts={sortOptions} sort={effectiveSort} onSort={setSort}/>

      {active === 'playlists' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <PinLinkBar T={T} spotify={spotify} onPinned={addPin}/>
          {pins.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <ALabel T={T}>
                Pinned
              </ALabel>
              <BrowseGrid>
                {pins.map((p) => (
                  <PinnedTile key={p.id} T={T} tile={p}
                    onPick={() => onPickItem?.(p)}
                    onRemove={() => removePin(p.id)}/>
                ))}
              </BrowseGrid>
            </div>
          )}
        </div>
      )}

      {loading && <BrowseStatus T={T}>Loading…</BrowseStatus>}
      {error   && <BrowseStatus T={T}>Library failed to load.</BrowseStatus>}
      {showEmpty && (
        <BrowseStatus T={T}>Nothing saved here yet.</BrowseStatus>
      )}
      {(tiles.length > 0 || active === 'playlists') && (
        <BrowseGrid>
          {active === 'playlists' && (
            <ContentTile T={T} title="Liked Songs" subtitle="Your saved tracks"
              imageUrl={null} seed="liked-songs"
              onClick={() => onPickItem?.({ kind: 'liked', id: 'liked', title: 'Liked Songs', seed: 'liked-songs' })}/>
          )}
          {tiles.map((tile, i) => (
            <ContentTile key={`${tile.id}-${i}`} T={T}
              title={tile.title} subtitle={tile.subtitle}
              imageUrl={tile.imageUrl} seed={tile.seed}
              onClick={() => onPickItem?.(tile)}/>
          ))}
        </BrowseGrid>
      )}
    </div>
  );
}

// SApple — the Apple Music library drill-in: parity with the Spotify library
// (Playlists / Albums / Artists tabs + sort) plus the Spotify→Apple sync. Reads
// *your* Apple library via the MUT-bridge (apple-lib). Read-only browse — tap a
// playlist/album for its tracks, an artist for their albums; playing Apple
// content on Sonos is a separate, not-yet-wired concern. "Sync from Spotify"
// migrates Spotify playlists into Apple, skipping any name that already exists
// (Apple's API can't prune or delete, so existing playlists are never touched).
export function SApple({ T, hass, getToken, onPickItem }) {
  const spotify = useSpotifyApi(getToken);
  const [filter, setFilter] = useState('playlists'); // playlists | albums | artists
  const [sort, setSort] = useState('recent');        // recent | az
  const [stack, setStack] = useState([]);            // detail nav: [{kind:'tracks'|'albums', q, name, playId, playKind, playArt}]
  const [sync, setSync] = useState(null);
  const cur = stack[stack.length - 1] || null;
  const list = useFetched(() => appleLib(hass, filter === 'playlists' ? '' : `?type=${filter}`), [hass, filter]);
  const detail = useFetched(() => cur ? appleLib(hass, cur.q) : Promise.resolve(null), [cur?.q]);
  const notConnected = list.error?.message === 'apple_not_connected';

  // One-time migration. Read both libraries, create every Spotify playlist whose
  // name isn't already in Apple, sequentially (one playlist at a time keeps us
  // clear of Apple's per-track search rate limit). Existing names are skipped,
  // never modified. Stops early if the MUT expires mid-run.
  async function runSync() {
    setSync({ running: true, phase: 'Reading your Spotify and Apple libraries…', total: 0, done: 0, created: [], skipped: [], unlisted: [], errors: [], error: null });
    try {
      const [libData, plData, me] = await Promise.all([appleLib(hass), spotify.myPlaylists(), spotify.getMe()]);
      const existing = new Set((libData.playlists || []).map((p) => (p.name || '').trim().toLowerCase()));
      const all = (plData.items || []).filter(Boolean);
      const missing = all.filter((p) => !existing.has((p.name || '').trim().toLowerCase()));
      // Spotify's Development Mode lists contents only for playlists you own
      // or collaborate on; followed ones are reported, not attempted.
      const toCreate = missing.filter((p) => canListItems(p, me?.id));
      const unlisted = missing.filter((p) => !canListItems(p, me?.id)).map((p) => p.name);
      const skipped = all.filter((p) => existing.has((p.name || '').trim().toLowerCase())).map((p) => p.name);
      const created = [], errors = [];
      setSync((s) => ({ ...s, total: toCreate.length, skipped, unlisted }));
      for (let i = 0; i < toCreate.length; i++) {
        const pl = toCreate[i];
        setSync((s) => ({ ...s, phase: `Syncing “${pl.name}”…`, done: i }));
        try {
          const t = await spotify.playlistTracks(pl.id);
          if (!t.length) errors.push({ name: pl.name, error: 'no playable tracks' });
          else {
            const res = await applePost(hass, 'apple-sync', { name: pl.name, tracks: t });
            created.push({ name: pl.name, count: res.playlist?.count ?? 0, missed: res.missed ?? 0 });
          }
        } catch (e) {
          errors.push({ name: pl.name, error: e.message });
          if (e.status === 401) { errors.push({ name: '—', error: 'Apple sign-in expired; re-mint in the app and rerun.' }); break; }
        }
        setSync((s) => ({ ...s, created: [...created], errors: [...errors] }));
      }
      setSync((s) => ({ ...s, running: false, phase: 'Done.', done: toCreate.length, created, errors }));
      list.refetch?.();
    } catch (e) {
      setSync((s) => ({ ...(s || {}), running: false, error: e.message }));
    }
  }

  // Tapping an Apple track plays the playlist/album from there, routed through
  // the card's shared onPickItem → dispatch (Apple branch) → the same room model
  // (focused room, or the S3 target picker) Spotify uses. apple-playlist/-album
  // kinds tell dispatch to use the ha-av apple_sonos.play service.
  const appleItem = (startArt) => ({
    kind: cur.playKind === 'album' ? 'apple-album' : 'apple-playlist',
    id: cur.playId, name: cur.name, imageUrl: cur.playArt || startArt || null,
  });

  if (sync) {
    const pct = sync.total ? Math.round((sync.done / sync.total) * 100) : 0;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* While the sync runs the button is the in-flight key (lifted,
            spinner, "Syncing…"); once done it is a quiet back key. */}
        <button onClick={() => setSync(null)} disabled={sync.running} style={quietKey(T, {
          alignSelf: 'flex-start', padding: '0 18px 0 12px',
          background: sync.running ? T.keyFill : T.cardSoft,
          boxShadow: sync.running ? T.keyShadow : 'none',
          cursor: sync.running ? 'default' : 'pointer',
        })}>
          {sync.running ? <BusyDot T={T}/> : <ChevronLeft/>}
          {sync.running ? 'Syncing…' : 'Back to library'}
        </button>

        <div style={{
          fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
          color: sync.error ? T.attention : T.ink,
        }}>
          {sync.error ? `Sync failed: ${sync.error}` : sync.phase}
        </div>
        {sync.total > 0 && (
          <div style={{ height: 8, borderRadius: 4, background: T.hair, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, borderRadius: 4, background: T.busy, transition: 'width .2s' }}/>
          </div>
        )}
        <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft, fontVariantNumeric: 'lining-nums tabular-nums' }}>
          {sync.created.length} created · {sync.skipped.length} already in Apple{(sync.unlisted || []).length ? ` · ${sync.unlisted.length} followed, not synced` : ''} · {sync.errors.length} issue{sync.errors.length === 1 ? '' : 's'}
        </div>

        {sync.created.length > 0 && (
          <SyncReportBlock T={T} title="Created">
            {sync.created.map((c, i) => (
              <div key={i}>{c.name} — {c.count} track{c.count === 1 ? '' : 's'}{c.missed ? ` (${c.missed} not on Apple)` : ''}</div>
            ))}
          </SyncReportBlock>
        )}
        {sync.skipped.length > 0 && (
          <SyncReportBlock T={T} title="Skipped — already in Apple">
            {sync.skipped.map((n, i) => <div key={i}>{n}</div>)}
          </SyncReportBlock>
        )}
        {(sync.unlisted || []).length > 0 && (
          <SyncReportBlock T={T} title="Not synced — Spotify won’t list playlists you follow">
            {sync.unlisted.map((n, i) => <div key={i}>{n}</div>)}
          </SyncReportBlock>
        )}
        {sync.errors.length > 0 && (
          <SyncReportBlock T={T} title="Issues">
            {sync.errors.map((e, i) => <div key={i}>{e.name}: {e.error}</div>)}
          </SyncReportBlock>
        )}
      </div>
    );
  }

  // Drill-in: a playlist/album's tracks, or an artist's albums.
  if (cur) {
    const isTracks = cur.kind === 'tracks';
    const trackList = detail.data?.tracks || [];
    const albumList = detail.data?.albums || [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button onClick={() => setStack((s) => s.slice(0, -1))} style={quietKey(T, {
          alignSelf: 'flex-start', padding: '0 18px 0 12px', maxWidth: '100%',
        })}>
          <ChevronLeft/>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{cur.name}</span>
        </button>
        {cur.playId && isTracks && (
          <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft, marginTop: 4 }}>
            Tap a track to play the {cur.playKind === 'album' ? 'album' : 'playlist'} from there.
          </div>
        )}
        {detail.loading && <BrowseStatus T={T}>Loading…</BrowseStatus>}
        {detail.error && <BrowseStatus T={T}>Couldn’t load.</BrowseStatus>}
        {isTracks ? (
          <>
            {!detail.loading && !detail.error && trackList.length === 0 && <BrowseStatus T={T}>No tracks.</BrowseStatus>}
            {trackList.map((t, i) => (
              <div key={t.id || i}
                onClick={() => onPickItem?.(appleItem(t.art), { startIndex: i, play: true })}
                style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '10px 4px', minHeight: 64,
                boxSizing: 'border-box',
                borderBottom: `1px solid ${T.hair}`, cursor: 'pointer',
              }}>
                <div style={{
                  width: 24, flexShrink: 0, textAlign: 'right', color: T.mute,
                  fontFamily: A_FONT.sans, fontSize: fs(T, 13), fontWeight: 500,
                  fontVariantNumeric: 'lining-nums tabular-nums',
                }}>{i + 1}</div>
                {t.art
                  ? <img src={t.art} alt="" width="44" height="44" style={{ borderRadius: 6, flex: 'none', background: T.cardSoft, objectFit: 'cover' }}/>
                  : <div style={{ width: 44, height: 44, borderRadius: 6, background: T.cardSoft, flex: 'none' }}/>}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500, color: T.ink, lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.title}</div>
                  <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.artist}</div>
                </div>
              </div>
            ))}
          </>
        ) : (
          <>
            {!detail.loading && !detail.error && albumList.length === 0 && <BrowseStatus T={T}>No albums.</BrowseStatus>}
            {albumList.length > 0 && (
              <BrowseGrid>
                {albumList.map((al) => (
                  <ContentTile key={al.id} T={T} title={al.name} subtitle={al.artist}
                    imageUrl={al.art} seed={al.id}
                    onClick={() => setStack((s) => [...s, { kind: 'tracks', q: `?album=${encodeURIComponent(al.id)}`, name: al.name, playId: al.id, playKind: 'album', playArt: al.art }])}/>
                ))}
              </BrowseGrid>
            )}
          </>
        )}
      </div>
    );
  }

  // Top-level browse — Playlists / Albums / Artists, sortable, + the sync button.
  const APPLE_FILTERS = [
    { key: 'playlists', label: 'Playlists' },
    { key: 'albums', label: 'Albums' },
    { key: 'artists', label: 'Artists' },
  ];
  const APPLE_SORTS = [
    { key: 'recent', label: 'Recently added' },
    { key: 'az', label: 'A–Z' },
  ];
  const d = list.data || {};
  let items = filter === 'playlists' ? (d.playlists || []) : filter === 'albums' ? (d.albums || []) : (d.artists || []);
  items = [...items];
  if (sort === 'az') items.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
  else if (filter !== 'artists') items.sort((a, b) => String(b.dateAdded || '').localeCompare(String(a.dateAdded || '')));
  const subtitleFor = (it) => filter === 'albums' ? it.artist : filter === 'artists' ? 'Artist' : 'Playlist';
  const onTile = (it) => {
    if (filter === 'albums') setStack([{ kind: 'tracks', q: `?album=${encodeURIComponent(it.id)}`, name: it.name, playId: it.id, playKind: 'album', playArt: it.art }]);
    else if (filter === 'artists') setStack([{ kind: 'albums', q: `?artist=${encodeURIComponent(it.id)}`, name: it.name }]);
    else setStack([{ kind: 'tracks', q: `?playlist=${encodeURIComponent(it.id)}`, name: it.name, playId: it.id, playKind: 'playlist', playArt: it.art }]);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {notConnected ? (
        <BrowseStatus T={T}>Apple Music isn’t connected. Open the HA Apple Music app on your phone and tap Connect.</BrowseStatus>
      ) : (
        <>
          <BrowseControls T={T}
            filters={APPLE_FILTERS} filter={filter} onFilter={setFilter}
            sorts={APPLE_SORTS} sort={sort} onSort={setSort}
            action={
              <button onClick={runSync} style={quietKey(T, { marginLeft: 'auto' })}>
                Sync from Spotify
              </button>
            }/>

          {list.loading && <BrowseStatus T={T}>Loading…</BrowseStatus>}
          {list.error && <BrowseStatus T={T}>Apple library failed to load.</BrowseStatus>}
          {!list.loading && !list.error && items.length === 0 && <BrowseStatus T={T}>Nothing here yet.</BrowseStatus>}
          {items.length > 0 && (
            <BrowseGrid>
              {items.map((it) => (
                <ContentTile key={it.id} T={T} title={it.name} subtitle={subtitleFor(it)}
                  imageUrl={it.art} seed={it.id} onClick={() => onTile(it)}/>
              ))}
            </BrowseGrid>
          )}
        </>
      )}
    </div>
  );
}

// Titled, scrollable block for the Spotify→Apple sync report.
function SyncReportBlock({ T, title, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <ALabel T={T}>{title}</ALabel>
      <div style={{
        fontFamily: A_FONT.sans, fontSize: fs(T, 14), color: T.inkSoft, lineHeight: 1.55,
        maxHeight: 200, overflowY: 'auto',
      }}>{children}</div>
    </div>
  );
}

// Filter + sort controls for the library drill-ins (Spotify and Apple).
// Both are mutually exclusive choices, so both are Segmented-style
// trays (ChoiceTray, below); the optional `action` (Apple's sync button) sits at the end of
// the sort row.
function BrowseControls({ T, filters, filter, onFilter, sorts, sort, onSort, action }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex' }}>
        <ChoiceTray T={T} ariaLabel="Show"
          options={filters.map((f) => ({ value: f.key, label: f.label }))}
          value={filter} onChange={onFilter}/>
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <ALabel T={T}>Sort</ALabel>
        <ChoiceTray T={T} ariaLabel="Sort"
          options={sorts.map((o) => ({ value: o.key, label: o.label }))}
          value={sort} onChange={onSort}/>
        {action}
      </div>
    </div>
  );
}

// ChoiceTray — Segmented's look (quiet tray, chosen option in the
// selected fill) with each option sized to its own label. Segmented
// splits its width equally, which clips "Recently added" beside "A–Z";
// at phone width three equal options of the longest label do not fit at
// all. Same tokens as Segmented so the two read as one control family.
function ChoiceTray({ T, options, value, onChange, ariaLabel, height = 44 }) {
  return (
    <div role="group" aria-label={ariaLabel} style={{
      display: 'inline-flex', padding: 3, borderRadius: RADIUS.control, background: T.cardSoft,
      maxWidth: '100%',
    }}>
      {options.map((o) => {
        const chosen = value === o.value;
        return (
          <button key={o.value} onClick={() => onChange(o.value)} aria-pressed={chosen}
            style={{
              flex: '0 0 auto', height, padding: '0 16px',
              border: 'none', borderRadius: RADIUS.key, cursor: 'pointer',
              background: chosen ? T.selected : 'transparent',
              color: chosen ? T.selectedInk : T.ink,
              fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
              whiteSpace: 'nowrap',
              transition: 'background .18s ease-out, color .18s ease-out',
            }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// Shared layout helpers for drill-in screens.
function BrowseGrid({ children }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
      gap: 16,
    }}>
      {children}
    </div>
  );
}
function BrowseStatus({ T, children }) {
  return (
    <div style={{
      fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
      color: T.inkSoft, padding: '32px 0', textAlign: 'center', lineHeight: 1.45,
    }}>{children}</div>
  );
}
